import authSettings from "@/shared/config/authSettings";

export const TARGET_URL: string = "target_url"; // special query param key for encoding the post-auth redirect target within auth flow URLs
export const AUTHORIZATION_ID_PARAM: string = "authorization_id"; // query param key for encoding the OAuth authorization ID for MCP consent flow

function getAllowedAppOrigins(): Set<string> {
  const origins = new Set<string>();

  if (typeof window !== "undefined" && window.location.origin) {
    origins.add(window.location.origin);
  }

  const siteUrl = process.env.SITE_URL;
  if (siteUrl) {
    try {
      origins.add(new URL(siteUrl).origin);
    } catch {
      // Ignore invalid SITE_URL and continue with runtime origin checks.
    }
  }

  return origins;
}

export function normalizeSafeInternalTarget(
  targetUrl: string | null | undefined,
): string | undefined {
  if (!targetUrl) return undefined;

  let normalizedTarget = targetUrl;

  if (!normalizedTarget.startsWith("/")) {
    try {
      const parsedTarget = new URL(normalizedTarget);
      if (!getAllowedAppOrigins().has(parsedTarget.origin)) {
        return undefined;
      }
      normalizedTarget = `${parsedTarget.pathname}${parsedTarget.search}${parsedTarget.hash}`;
    } catch {
      return undefined;
    }
  }

  if (!normalizedTarget.startsWith("/")) return undefined;
  if (normalizedTarget.startsWith("//")) return undefined;

  // Prevent auth-page loops after successful login/signup.
  if (
    normalizedTarget.startsWith(authSettings.redirects.login) ||
    normalizedTarget.startsWith(authSettings.redirects.register)
    //  || normalizedTarget.startsWith("/logout") //! do not have this page. Adding it here if ever implemented
  ) {
    return undefined;
  }

  return normalizedTarget;
}

export function buildAuthRouteHref(
  pathname: string,
  targetUrl?: string,
): string {
  const normalizedTargetUrl = normalizeSafeInternalTarget(targetUrl);
  if (!normalizedTargetUrl) {
    return pathname;
  }
  return `${pathname}?${TARGET_URL}=${encodeURIComponent(normalizedTargetUrl)}`;
}

export function isMcpConsentAuthorizationTarget(
  targetUrl: string | null | undefined,
): targetUrl is string {
  const normalizedTargetUrl = normalizeSafeInternalTarget(targetUrl);
  if (!normalizedTargetUrl) {
    return false;
  }

  // Preserve full browser navigation for one-time OAuth authorization IDs.
  return normalizedTargetUrl.startsWith(
    `${authSettings.redirects.oauthConsent}?${AUTHORIZATION_ID_PARAM}=`,
  );
}
